/* =====================================================================
 * ToggleSwitch — theme 기반 공통 토글 스위치 컴포넌트
 * ===================================================================== */

import React from 'react';
import styled, { css, keyframes } from 'styled-components';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Track = styled.button<{ $checked: boolean; $disabled: boolean; $size: 'sm' | 'md' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  border: none;
  outline: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border-radius: 999px;
  transition: background ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          width: 36px;
          height: 20px;
          padding: 2px;
        `
      : css`
          width: 44px;
          height: 24px;
          padding: 2px;
        `}

  /* on 상태는 테넌트 brand 색을 쓴다 — success(초록)는 "성공" 피드백 색이라
   * 브랜드가 초록이 아닌 배포에서 혼자 튄다. */
  ${({ $checked, theme }) =>
    $checked
      ? css`
          background: ${theme.colors.brand};
        `
      : css`
          background: ${theme.colors.bgTertiary};
          border: 1px solid ${theme.colors.border};
        `}

  &:hover:not(:disabled) {
    ${({ $checked, theme }) =>
      $checked
        ? css`
            background: ${theme.colors.brandHover};
          `
        : css`
            background: ${theme.colors.bgHover};
          `}
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.accent}33;
  }
`;

const Thumb = styled.div<{ $checked: boolean; $size: 'sm' | 'md'; $loading: boolean }>`
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform ${({ theme }) => theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          width: 16px;
          height: 16px;
        `
      : css`
          width: 20px;
          height: 20px;
        `}

  ${({ $checked, $size }) =>
    $checked
      ? css`
          transform: translateX(${$size === 'sm' ? '16px' : '20px'});
        `
      : css`
          transform: translateX(0);
        `}
`;

const LoadingSpinner = styled.div<{ $size: 'sm' | 'md' }>`
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: ${({ theme }) => theme.colors.brand};
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;

  ${({ $size }) =>
    $size === 'sm'
      ? css`
          width: 10px;
          height: 10px;
        `
      : css`
          width: 12px;
          height: 12px;
        `}
`;

const Wrapper = styled.div<{ $hasLabel: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  ${({ $hasLabel }) =>
    $hasLabel &&
    css`
      cursor: pointer;
    `}
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  user-select: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.descFontSize};
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export interface ToggleSwitchProps {
  'aria-label'?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  size?: 'sm' | 'md';
  label?: string;
  labelPosition?: 'left' | 'right';
  className?: string;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  'aria-label': ariaLabel,
  checked,
  onChange,
  disabled = false,
  loading = false,
  size = 'sm',
  label,
  labelPosition = 'left',
  className,
}) => {
  const isDisabled = disabled || loading;

  const handleClick = () => {
    if (!isDisabled) {
      onChange(!checked);
    }
  };

  const toggle = (
    <Track
      $checked={checked}
      $disabled={isDisabled}
      $size={size}
      disabled={isDisabled}
      onClick={handleClick}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel || label || 'toggle'}
    >
      <Thumb $checked={checked} $size={size} $loading={loading}>
        {loading && <LoadingSpinner $size={size} />}
      </Thumb>
    </Track>
  );

  if (!label) {
    return <div className={className}>{toggle}</div>;
  }

  return (
    <Wrapper $hasLabel={!!label} className={className} onClick={handleClick}>
      {labelPosition === 'left' && <Label>{label}</Label>}
      {toggle}
      {labelPosition === 'right' && <Label>{label}</Label>}
    </Wrapper>
  );
};
